import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function WinterSnowshoeingVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "winter-snowshoeing-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Grouse Mountain",
    "section-2": "Dog Mountain (Mount Seymour)",
    "section-3": "Hollyburn Mountain (Cypress)",
    "section-4": "Cypress Mountain Nordic Area",
    "section-5": "Bowen Lookout (Cypress)",
    "section-6": "Sea to Sky Gondola",
    "section-7": "Elfin Lakes (Garibaldi Provincial Park)",
    "local-regulations": "Local Regulations & Best Practices",
    "section-step-by-step": "Step-by-Step Guide",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://bcparks.ca",
      label: "BC Parks (Official Site)",
    },
    {
      url: "https://www.adventuresmart.ca",
      label: "AdventureSmart (Trip Planning Resources)",
    },
    {
      url: "https://avalanche.ca",
      label: "Avalanche Canada (Forecasts & Education)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // The "id" matches the section ID. 
  // The lat/lng match exactly what is used in the <Marker> tag.
  const locations = [
    {
      id: "section-1",
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain (North Vancouver)",
    },
    {
      id: "section-2",
      lat: 49.36678,
      lng: -122.94825,
      popupText: "Dog Mountain (Mount Seymour)",
    },
    {
      id: "section-3",
      lat: 49.37927,
      lng: -123.19164,
      popupText: "Hollyburn Mountain (Cypress)",
    },
    {
      id: "section-4",
      lat: 49.37927,
      lng: -123.19164,
      popupText: "Cypress Mountain Nordic Area",
    },
    {
      id: "section-5",
      lat: 49.39725,
      lng: -123.20567,
      popupText: "Bowen Lookout (Cypress)",
    },
    {
      id: "section-6",
      lat: 49.67528,
      lng: -123.15694,
      popupText: "Sea to Sky Gondola (Squamish)",
    },
    {
      id: "section-7",
      lat: 49.78556,
      lng: -122.98889,
      popupText: "Elfin Lakes (Garibaldi Provincial Park)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Winter Snowshoeing in Vancouver: Top Trails & Practical Tips</title>
        <meta
          name="description"
          content="Explore Vancouver's top winter snowshoe trails with this complete guide covering locations, local regulations, step-by-step tips, FAQs, and more."
        />
        <meta
          name="keywords"
          content="vancouver, snowshoeing, winter sports, cypress, grouse, mount seymour, elfin lakes"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/winter-snowshoeing-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Winter Snowshoeing in Vancouver: Top Trails & Practical Tips"
        />
        <meta
          property="og:description"
          content="Explore Vancouver's top winter snowshoe trails, safety guidelines, and essential tips in one comprehensive guide."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/winter-snowshoeing-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/winter-snowshoeing-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Winter Snowshoeing in Vancouver: Top Trails & Practical Tips"
        />
        <meta
          name="twitter:description"
          content="Explore Vancouver's top winter snowshoe trails, safety guidelines, and essential tips in one comprehensive guide."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/winter-snowshoeing-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt={ref_data?.["image-desc"] || "Winter Snowshoeing in Vancouver"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s proximity to snow-covered mountains makes it a
              winter paradise for snowshoe enthusiasts. Within minutes from
              downtown, you can be trekking through alpine forests or standing
              atop a peak overlooking the city. Snowshoeing is beginner-friendly,
              low-impact, and provides a great way to experience the magic of
              winter without the need for skiing or snowboarding skills. In this
              guide, you’ll discover the top snowshoe trails around Vancouver,
              local regulations, essential preparation tips, FAQs, and resources
              to help you make the most of your winter adventure.
            </p>

            {/* SECTION 1: Grouse Mountain */}
            <h2 id="section-1">Grouse Mountain</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Grouse Mountain, North Vancouver{" "}
              <Marker
                coordY={49.37956}
                coordX={-123.08180}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Known as the “Peak of Vancouver,” Grouse Mountain features well-marked
              snowshoe trails with stunning views of the city and coastal mountains.
              The Blue Grouse Loop is an easy, scenic trail through alpine forest,
              while the Snowshoe Grind offers a moderate workout. A mountain admission
              ticket is required to access the summit via gondola; once at the top,
              you’ll find rentals, restaurants, and guided tours. It’s a convenient
              spot for those wanting a structured, patrolled snowshoe experience.
            </p>

            {/* SECTION 2: Dog Mountain (Mount Seymour) */}
            <h2 id="section-2">Dog Mountain (Mount Seymour)</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Dog Mountain Trailhead, Mount Seymour{" "}
              <Marker
                coordY={49.36678}
                coordX={-122.94825}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              An easy, family-friendly trail in Mount Seymour Provincial Park, Dog Mountain
              is popular for its relatively flat terrain and panoramic city views from
              its bluff. The 5–6 km round-trip route often has well-packed snow, though
              snowshoes are recommended after fresh snowfall. Mount Seymour requires a
              free day-use permit in winter, so reserve yours online. Bring a camera to
              capture the epic viewpoint at the end!
            </p>

            {/* SECTION 3: Hollyburn Mountain (Cypress) */}
            <h2 id="section-3">Hollyburn Mountain (Cypress)</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Hollyburn Mountain Trailhead, Cypress Provincial Park{" "}
              <Marker
                coordY={49.37927}
                coordX={-123.19164}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              A North Shore classic, Hollyburn Mountain offers a moderately challenging
              route with about 7.5 km round-trip and rewarding summit views of Vancouver,
              The Lions peaks, and beyond. The trail begins near Cypress’ Nordic area
              and ascends through forest to open slopes. Although it is free, you are
              responsible for your own safety in this backcountry terrain. Check avalanche
              conditions before you go, and dress warmly.
            </p>

            {/* SECTION 4: Cypress Mountain Nordic Area */}
            <h2 id="section-4">Cypress Mountain Nordic Area</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Nordic Area, Cypress Mountain{" "}
              <Marker
                coordY={49.37927}
                coordX={-123.19164}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              For those seeking groomed snowshoe trails, Cypress offers an 11 km network
              patrolled and maintained by the resort. A paid snowshoe trail pass is required.
              Beginners will appreciate the flat loops around First Lake, while more experienced
              trekkers can head to Hollyburn Lodge for a mid-hike break. Amenities like rentals,
              a café, and patrol services make this an accessible entry point to snowshoeing.
              Note that dogs are not allowed on the groomed trails.
            </p>

            {/* SECTION 5: Bowen Lookout (Cypress) */}
            <h2 id="section-5">Bowen Lookout (Cypress)</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Bowen Lookout Trail, Cypress Provincial Park{" "}
              <Marker
                coordY={49.39725}
                coordX={-123.20567}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              A short, rewarding trail offering a spectacular view of Howe Sound and Bowen Island,
              Bowen Lookout is ideal for beginners seeking a quick snowshoe outing. The route is
              just 4 km round-trip with minimal elevation gain. A free backcountry access pass from
              Cypress may be required since it begins near the ski area boundary. This location is
              especially scenic on clear days and makes for excellent photos.
            </p>

            {/* SECTION 6: Sea to Sky Gondola */}
            <h2 id="section-6">Sea to Sky Gondola</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Summit Lodge, Squamish{" "}
              <Marker
                coordY={49.67528}
                coordX={-123.15694}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Located in Squamish, the Sea to Sky Gondola whisks you to scenic alpine terrain
              without the climb. The Panorama Trail is an easy loop featuring suspension bridges,
              forested paths, and breathtaking vistas of Howe Sound. Tickets are required to ride
              the gondola, and once at the top, you’ll find a lodge, rentals, and family-friendly
              routes. A must-visit for those seeking a blend of convenience and sweeping views.
            </p>

            {/* SECTION 7: Elfin Lakes (Garibaldi Provincial Park) */}
            <h2 id="section-7">Elfin Lakes (Garibaldi Provincial Park)</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong> Elfin Lakes, Garibaldi Provincial Park{" "}
              <Marker
                coordY={49.78556}
                coordX={-122.98889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              For a true backcountry adventure, Elfin Lakes is a longer 22 km round-trip
              snowshoe route through pristine winter landscapes. Expect stunning mountain
              views, a warming shelter at Red Heather, and a hut at the lakes. Ideal for
              experienced snowshoers with avalanche training, Elfin Lakes is free for day use
              but requires reservations for overnight stays. Pack all winter essentials
              and be prepared for changing conditions.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Vancouver’s winter trail systems often have permit requirements to manage
              crowds and protect the environment. Mount Seymour enforces a free day-use
              pass, while Cypress may require backcountry access passes near the ski area.
              Always follow signage, stick to designated trails, and check BC Parks or resort
              websites for the latest updates. Practice Leave No Trace principles, pack out
              your garbage, and be aware of potential avalanche areas on unpatrolled routes.
              Remember to carry the “Ten Essentials” including a headlamp, extra clothing,
              navigation tools, and first aid. Always let someone know your trip plan and
              check Avalanche Canada for risk levels before venturing out.
            </p>

            {/* SECTION: Step-by-Step Guide */}
            <h2 id="section-step-by-step">Step-by-Step Guide</h2>
            <ol>
              <li>
                <strong>Pick the right trail:</strong> Choose a route that matches your
                fitness and experience. Beginners may opt for Dog Mountain or Cypress’s
                groomed loops; advanced users might tackle Elfin Lakes.
              </li>
              <li>
                <strong>Check conditions & permits:</strong> Verify weather, avalanche
                forecasts, and required day-use or backcountry passes. Road conditions can
                be icy, so ensure you have proper winter tires.
              </li>
              <li>
                <strong>Assemble your gear:</strong> Dress in layers, bring waterproof
                boots, gaiters, snowshoes, poles, and the Ten Essentials. For unpatrolled
                routes, pack avalanche gear if necessary.
              </li>
              <li>
                <strong>Start early:</strong> Daylight is shorter in winter. Aim for a
                morning start to avoid descending in the dark and to secure parking.
              </li>
              <li>
                <strong>Stay on marked trails:</strong> Follow trail markers or a reliable
                GPS track, especially in poor visibility. Turn around if conditions worsen.
              </li>
              <li>
                <strong>Descend safely:</strong> Use caution on icy or steep sections.
                Switch to microspikes if needed. Keep an eye on time so you’re out before
                dark.
              </li>
            </ol>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a permit to snowshoe in Vancouver’s parks?</h3>
              <p>
                Many provincial parks in the region require free day-use passes (like
                Mount Seymour). Grouse and Cypress resorts have their own ticketing or
                passes. Always check official websites before going.
              </p>

              <h3>2. What gear is essential for snowshoeing?</h3>
              <p>
                You’ll need waterproof boots, snowshoes, warm layered clothing,
                trekking poles (optional but helpful), and the Ten Essentials (navigation,
                first aid, headlamp, extra clothing, food, water, etc.). Avalanche gear
                (beacon, probe, shovel) is necessary for higher-risk terrain.
              </p>

              <h3>3. Is snowshoeing safe for beginners?</h3>
              <p>
                Yes, on well-marked, low-risk routes like Dog Mountain or Cypress
                Nordic. Start with an easy trail, dress appropriately, and bring
                basic safety gear. Always check the current conditions and let
                someone know your plan.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Winter snowshoeing around Vancouver offers everything from family-friendly
              forest strolls to challenging backcountry epics. With the right gear,
              preparation, and respect for local regulations, you can enjoy breathtaking
              vistas and tranquil snow-covered landscapes just minutes from the city.
              Whether you choose an easy loop or a multi-hour trek, Vancouver’s snowy
              mountains await your footsteps. Stay safe, follow best practices, and
              embrace the winter wonderland in Vancouver’s backyard!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}